import { Injectable, Injector } from '@angular/core';
import { ApplicationService } from '@app/shared/application/application.service';
import { RequirementConfigurationService } from '@app/shared/services/requirement-configuration.service';
import { Observable, of, ReplaySubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ApplicationStatus, ApplicationUploadFilesInput, CreateOrUpdateSplPostgraduateTitlesInput, CreateSplInput, CreateSplOutput, ExternalSystemEnum, GeneralFileDto, GeneralFileInfoDto, GetSplDocumentsInput, GetSplFileInput, GetSplOutput, LiaTypeServiceProxy, PersonApplicationSummaryDto, RequirementDto, RequirementType, SplDocumentServiceProxy, SplDocumentsOutput, SplDto, SplNarcoticKnowledgeDto, SplNarcoticKnowledgeServiceProxy, SplPersonDto, SplPersonServiceProxy, SplPostgraduateTitleDto, SplPostgraduateTitleServiceProxy, SplServiceProxy, SplStatus } from './../../../shared/service-proxies/service-proxies';
import { AppComponentBase } from '@shared/common/app-component-base';

export class SplInvoiceAddressDetail {
    name!: string | undefined;
    street!: string;
    streetNr!: string | undefined;
    postCode!: string;
    city!: string;
    email!: string;
    countryId!: string | undefined;

    constructor(data?: SplInvoiceAddressDetail) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property)){
                    (<any>this)[property] = (<any>data)[property];
                }
            }
        }
    }
}

@Injectable({providedIn: 'root'})
export class SplService extends AppComponentBase {

    spl: SplDto;
    splResultDocuments: GeneralFileInfoDto[];
    onlyPrivateAddressAllowed = false;

    private splsForPersonSubject = new ReplaySubject<PersonApplicationSummaryDto[]>(1);
    readonly splsForPerson$ = this.splsForPersonSubject.asObservable();

    constructor(
        injector: Injector,
        private applicationService: ApplicationService,
        private requirementConfigurationService: RequirementConfigurationService,
        private liaService: LiaTypeServiceProxy,
        private splService: SplServiceProxy,
        private personService: SplPersonServiceProxy,
        private narcosticKnowledgeService: SplNarcoticKnowledgeServiceProxy,
        private postgraduateTitleService: SplPostgraduateTitleServiceProxy,
        private documentService: SplDocumentServiceProxy,
    ) {
        super(injector);
    }

    loadSplsForPerson(gln: string): Observable<PersonApplicationSummaryDto[]> {
        if (gln && this.isGranted('Pages.Authority.Applications.Person')) {
            return this.splService.getSplsForPerson(gln).pipe(
                tap(spls => this.splsForPersonSubject.next(spls))
            );
        } else {
            this.splsForPersonSubject.next([]);
            return of([]);
        }
    }

    getExternalSystemByProfessionId(professionId: string): Observable<ExternalSystemEnum> {
        return this.liaService.getExternalRegisterByProfessionId(professionId);
    }

    createSpl(input: CreateSplInput): Observable<CreateSplOutput> {
        return this.splService.createSpl(input).pipe(tap((output: CreateSplOutput) => {
            this.spl = null;
        }));
    }

    deleteSpl(): Observable<void> {
        return this.applicationService.deleteApplication();
    }

    getSpl(caseId: string): Observable<GetSplOutput> {
        this.spl = null;
        return this.splService.getSpl(caseId).pipe(tap((output: GetSplOutput) => {
            this.spl = output.spl;
            return this.requirementConfigurationService.getRequirementByProfessionAndType(this.codes.profession.physician, RequirementType.OnlyPrivateAddressAllowed).subscribe((requirement: RequirementDto) => {
                this.onlyPrivateAddressAllowed = requirement.value === this.codes.yesNo.yes;
            });
        }));
    }

    closeSpl(): void {
        this.spl = null;
    }

    getMySpls(): Observable<PersonApplicationSummaryDto[]> {
        return this.splService.getMySpls();
    }

    getSplsForPerson(gln: string): Observable<PersonApplicationSummaryDto[]> {
        return this.splService.getSplsForPerson(gln);
    }

    isInSplStatus(expectedStatus: SplStatus): boolean {
        return this.spl.statusId === expectedStatus;
    }

    getProfessionalLicencePersonIdForSpl(): Observable<number> {
        return this.splService.getProfessionalLicencePersonIdForSpl(this.caseId);
    }

    getEvaluationComments(): Observable<string> {
        return this.splService.getEvaluationComments(this.caseId);
    }

    releaseSpl(comment: string): Observable<void> {
        return this.splService.releaseSpl(this.spl.application.caseId, comment);
    }

    getNextStatus(status: SplStatus): Observable<SplStatus> {
        let isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.splService.getNextStatus(this.caseId, status).pipe(map(status => {
            // Authority goes to 'finish review' and not to 'release'
            if (isAuthority && status == SplStatus.Release) {
                return SplStatus.FinishReview;
            }
            return status;
        }));
    }

    getStepUrl(nextStep: SplStatus): string {
        return this.applicationService.getNextStep(SplService.getUrlForStep(nextStep));
    }

    getFile(step: SplStatus, id: number): Observable<GeneralFileDto> {
        const input = new GetSplFileInput();
        input.caseId = this.caseId;
        input.step = step;
        input.id = id;
        return this.documentService.getFile(input);
    }

    getDocuments(step: SplStatus): Observable<SplDocumentsOutput> {
        const input = new GetSplDocumentsInput();
        input.caseId = this.caseId;
        input.step = step;
        return this.documentService.getDocuments(input);
    }

    uploadFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.documentService.uploadFiles(input);
    }

    deleteFile(step: SplStatus, id: number): Observable<void> {
        return this.documentService.deleteFile(this.caseId, step, id);
    }

    get caseId(): string {
        return this.applicationService.caseId;
    }

    get status(): ApplicationStatus {
        return this.applicationService.status;
    }

    setSplPersonStepToReview() {
        this.applicationService.setStepToReview(SplStatus.Person);
    }

    getPerson(): Observable<SplPersonDto> {
        return this.personService.get(this.caseId);
    }

    createOrUpdatePerson(person: SplPersonDto): Observable<any> {
        this.applicationService.setStepToReview(SplStatus.Person);
        return this.personService.createOrUpdate(this.caseId, person).pipe(tap((newStatusId: SplStatus) => {
            this.spl.statusId = newStatusId;
        }));
    }

    setSplNarcoticKnowledgeStepToReview() {
        this.applicationService.setStepToReview(SplStatus.NarcoticKnowledge);
    }

    getNarcoticKnowledge(): Observable<SplNarcoticKnowledgeDto> {
        return this.narcosticKnowledgeService.get(this.caseId);
    }

    createOrUpdateNarcoticKnowledge(narcoticKnowledge: SplNarcoticKnowledgeDto): Observable<any> {
        this.applicationService.setStepToReview(SplStatus.NarcoticKnowledge);
        return this.narcosticKnowledgeService.createOrUpdate(this.caseId, narcoticKnowledge).pipe(tap((newStatusId: SplStatus) => {
            this.spl.statusId = newStatusId;
        }));
    }

    setSplPostgraduateTitleStepToReview() {
        this.applicationService.setStepToReview(SplStatus.PostgraduateTitle);
    }

    getPostgraduateTitles(): Observable<SplPostgraduateTitleDto[]> {
        return this.postgraduateTitleService.get(this.caseId);
    }

    createOrUpdatePostgraduateTitle(postgraduateTitle: CreateOrUpdateSplPostgraduateTitlesInput): Observable<any> {
        this.applicationService.setStepToReview(SplStatus.PostgraduateTitle);
        return this.postgraduateTitleService.createOrUpdate(this.caseId, postgraduateTitle).pipe(tap((newStatusId: SplStatus) => {
            this.spl.statusId = newStatusId;
        }));
    }

    //createOrUpdateLiaPersonWithRegisterCheck(input: CreateOrUpdateLiaPersonWithRegisterCheckInput): Observable<CreateOrUpdateLiaPersonOutputDto> {
    //    this.setStepToReview(LiaStatus.Person);
    //    return this.liaPersonService.createOrUpdateLiaPersonWithRegisterCheck(input).pipe(tap((output: CreateOrUpdateLiaPersonOutputDto) => {
    //        this.lia.liaPerson = output.liaPerson;
    //    }));
    //}

    //acceptRegisterCheck(): Observable<LiaStatus> {
    //    this.setStepToReview(LiaStatus.Person);
    //    return this.liaPersonService.acceptRegisterCheck(this.caseId).pipe(tap((status: LiaStatus) => {
    //        this.lia.currentStep = status;
    //    }));
    //}

    checkDocuments(): Observable<SplStatus[]> {
        return this.splService.validateThatAllMandatoryDocumentsHaveBeenUploaded(this.caseId);
    }

    static getUrlForStep(step: SplStatus): string {
        switch (step) {
            case SplStatus.Person:
                return 'person';
            case SplStatus.NarcoticKnowledge:
                return 'narcotic-knowledge';
            case SplStatus.PostgraduateTitle:
                return 'postgraduate-title';
            case SplStatus.Release:
                return 'release';
            case SplStatus.FinishReview:
                return 'finish-review';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }
}
